import {getLocalizationBasedOnLang} from '../../../data/models/User/UserInfo';
import type {StudioFontCategory} from '../types';

/* The video branding panel (SourceBrandSummary) and the logo file checks behind it. */

const english = {
  videoBranding: 'Video branding',
  introEdit: 'Choose the colors and style for your video.',
  introWorkspace:
    "This video's branding starts from your workspace's brand in Settings, with any changes you made.",
  introWorkspaceDocument:
    "This video's branding starts from your workspace's brand in Settings and the style of your document.",
  introChosen: 'We use the logo and colors you chose to brand this video.',
  introDocument:
    'We use the logo, colors and style found in your document to brand this video.',

  // The logo.
  logoUnavailable: 'Logo unavailable',
  uploadedLogo: 'Uploaded logo',
  workspaceLogo: 'Workspace logo',
  workspaceLogoFromSettings: 'Workspace logo · from Settings',
  logo: 'Logo',
  documentLogoAlt: 'Logo extracted from the document',
  documentLogo: (page?: number) =>
    `Document logo${page ? ` · page ${page}` : ''}`,
  replace: 'Replace',
  uploading: 'Uploading…',
  remove: 'Remove',
  removing: 'Removing…',
  uploadLogo: 'Upload logo',
  noLogoRemoved: 'This video has no logo',
  noLogoYet: 'No logo yet',
  noLogoInDocument: 'No logo found in your document',
  uploadToAddBack: 'Upload one to add it back. PNG, JPG, SVG or WebP.',
  uploadOrContinue:
    'Upload one, or continue without it. PNG, JPG, SVG or WebP.',
  // Storyboards drafted before the wording change carry the worker's old sentence.
  noReliableLogo:
    "We couldn't identify a logo. You can upload one or continue without it.",

  // The colors.
  brandColors: 'Brand colors',
  detectedBrandColors: 'Detected brand colors',
  primary: 'Primary',
  secondary: 'Secondary',
  defaultColor: 'Default',
  apply: 'Apply',
  saving: 'Saving…',
  waitForSaving: 'Wait for the video branding to finish saving.',
  cancel: 'Cancel',
  applyColor: (role: string) => `Apply the ${role.toLowerCase()} color`,
  cancelColor: (role: string) =>
    `Cancel the ${role.toLowerCase()} color change`,
  colorHint:
    'Changes apply the next time you render, including the illustrations. We adjust shades so text stays readable.',
  invalidHex: 'Enter a hex color such as #1E6F6B.',

  // The font, chosen from the ones Studio offers.
  font: 'Font',
  fontCategories: {
    sans: 'Sans-serif',
    serif: 'Serif',
    display: 'Display',
    handwriting: 'Handwriting',
    monospace: 'Monospace',
  } as Record<StudioFontCategory, string>,
  fontOption: (name: string, category: string) => `${name} · ${category}`,
  // Brands detected before fonts could be chosen: the video matches the document's serif.
  matchedSerif: 'Matched to your document (serif)',
  fontHint:
    'The new font applies the next time you render. The illustrations are not redrawn.',
  fontMatched: "Your document's font is matched approximately.",
  chooseFontHint: 'Choose a font to use one Studio offers.',

  // The style found in the document.
  style: 'Style',
  typography: 'Typography',
  corners: 'Corners',
  fontApproximate: 'Font matching is approximate.',
  classicMinimal: 'Classic, minimal',
  modernMinimal: 'Modern, minimal',

  // Logo files.
  logoType: 'Choose a PNG, JPG, SVG or WebP image for your logo.',
  logoTooLarge: 'That image is larger than 10 MB. Choose a smaller logo.',
  logoUnreadable: "That image couldn't be read. Try a PNG or JPG.",
  logoBrowser: "Your browser couldn't prepare the logo.",
  logoTooDetailed: 'That logo is too detailed to upload. Try a simpler image.',
};

const welsh: typeof english = {
  videoBranding: 'Brandio’r fideo',
  introEdit: 'Dewiswch y lliwiau a’r arddull ar gyfer eich fideo.',
  introWorkspace:
    'Mae brandio’r fideo hwn yn dechrau o frand eich gweithle yn y Gosodiadau, gydag unrhyw newidiadau a wnaethoch.',
  introWorkspaceDocument:
    'Mae brandio’r fideo hwn yn dechrau o frand eich gweithle yn y Gosodiadau ac arddull eich dogfen.',
  introChosen:
    'Rydym yn defnyddio’r logo a’r lliwiau a ddewisoch i frandio’r fideo hwn.',
  introDocument:
    'Rydym yn defnyddio’r logo, y lliwiau a’r arddull a ganfuwyd yn eich dogfen i frandio’r fideo hwn.',

  logoUnavailable: 'Nid yw’r logo ar gael',
  uploadedLogo: 'Logo wedi’i uwchlwytho',
  workspaceLogo: 'Logo’r gweithle',
  workspaceLogoFromSettings: 'Logo’r gweithle · o’r Gosodiadau',
  logo: 'Logo',
  documentLogoAlt: 'Logo a gymerwyd o’r ddogfen',
  documentLogo: (page?: number) =>
    `Logo’r ddogfen${page ? ` · tudalen ${page}` : ''}`,
  replace: 'Amnewid',
  uploading: 'Yn uwchlwytho…',
  remove: 'Tynnu',
  removing: 'Yn tynnu…',
  uploadLogo: 'Uwchlwytho logo',
  noLogoRemoved: 'Does dim logo gan y fideo hwn',
  noLogoYet: 'Dim logo eto',
  noLogoInDocument: 'Ni chafwyd hyd i logo yn eich dogfen',
  uploadToAddBack:
    'Uwchlwythwch un i’w ychwanegu’n ôl. PNG, JPG, SVG neu WebP.',
  uploadOrContinue:
    'Uwchlwythwch un, neu parhewch hebddo. PNG, JPG, SVG neu WebP.',
  noReliableLogo:
    'Doedd dim modd adnabod logo. Gallwch uwchlwytho un neu barhau hebddo.',

  brandColors: 'Lliwiau’r brand',
  detectedBrandColors: 'Lliwiau’r brand a ganfuwyd',
  primary: 'Cynradd',
  secondary: 'Eilaidd',
  defaultColor: 'Rhagosodedig',
  apply: 'Defnyddio',
  saving: 'Yn cadw…',
  waitForSaving: 'Arhoswch nes bod brandio’r fideo wedi gorffen cadw.',
  cancel: 'Canslo',
  applyColor: (role: string) => `Defnyddio’r lliw ${role.toLowerCase()}`,
  cancelColor: (role: string) =>
    `Canslo’r newid i’r lliw ${role.toLowerCase()}`,
  colorHint:
    'Daw newidiadau i rym y tro nesaf y byddwch yn cynhyrchu, gan gynnwys y darluniau. Rydym yn addasu’r arlliwiau fel bod y testun yn dal yn ddarllenadwy.',
  invalidHex: 'Rhowch liw hecs fel #1E6F6B.',

  font: 'Ffont',
  fontCategories: {
    sans: 'Sans seriff',
    serif: 'Seriff',
    display: 'Arddangos',
    handwriting: 'Llawysgrifen',
    monospace: 'Unlled',
  },
  fontOption: (name: string, category: string) => `${name} · ${category}`,
  matchedSerif: 'Wedi’i baru â’ch dogfen (seriff)',
  fontHint:
    'Daw’r ffont newydd i rym y tro nesaf y byddwch yn cynhyrchu. Nid yw’r darluniau’n cael eu hail-lunio.',
  fontMatched: 'Mae ffont eich dogfen wedi’i baru’n fras.',
  chooseFontHint: 'Dewiswch ffont i ddefnyddio un y mae Studio yn ei gynnig.',

  style: 'Arddull',
  typography: 'Teipograffeg',
  corners: 'Corneli',
  fontApproximate: 'Mae’r ffont wedi’i baru’n fras.',
  classicMinimal: 'Clasurol, minimalaidd',
  modernMinimal: 'Modern, minimalaidd',

  logoType: 'Dewiswch ddelwedd PNG, JPG, SVG neu WebP ar gyfer eich logo.',
  logoTooLarge: 'Mae’r ddelwedd honno’n fwy na 10 MB. Dewiswch logo llai.',
  logoUnreadable:
    'Doedd dim modd darllen y ddelwedd honno. Rhowch gynnig ar PNG neu JPG.',
  logoBrowser: 'Doedd eich porwr ddim yn gallu paratoi’r logo.',
  logoTooDetailed:
    'Mae’r logo hwnnw’n rhy fanwl i’w uwchlwytho. Rhowch gynnig ar ddelwedd symlach.',
};

export const sourceBrandText: typeof english = getLocalizationBasedOnLang(
  english,
  welsh,
);
